import React, { useState } from "react";
import { Col, Container, Form, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { useFetchData, useSetDataTypeOnStationChange, useSetDaysOnPerspectiveChange } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DataTypePicker from "../components/Pickers/DataTypePicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import StationPicker from "../components/Pickers/StationPicker";
import { useUserFormState } from "../components/UserSelection";
import { DataContent, InputSelection, ModulePage } from "../components/pageSections";
import DashboardMusic from "./DashboardMusic";
import DashboardRadio from "./DashboardRadio";

const extraDefaults = { dashboardType: "radio" };

export default function Dashboard() {
    const moduleName = "dashboard";
    const [dashboardTypeLoaded, setDashboardTypeLoaded] = useState(null);

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);

    // Radio
    const [daypartLoading, daypartErrorCode, daypartData, fetchDaypartData] = useFetchData();
    const [contentBalanceLoading, contentBalanceErrorCode, contentBalanceData, fetchContentBalanceData] = useFetchData();
    const [breaksLoading, breaksErrorCode, breaksData, fetchBreaksData] = useFetchData();

    // Music
    const [top50OverlapLoading, top50OverlapErrorCode, top50OverlapData, fetchTop50OverlapData] = useFetchData();
    const [top50OverlapTrendLoading, top50OverlapTrendErrorCode, top50OverlapTrendData, fetchTop50OverlapTrendData] = useFetchData();
    const [allSongsOverlapLoading, allSongsOverlapErrorCode, allSongsOverlapData, fetchAllSongsOverlapData] = useFetchData();
    const [allSongsOverlapTrendLoading, allSongsOverlapTrendErrorCode, allSongsOverlapTrendData, fetchAllSongsOverlapTrendData] = useFetchData();
    const [marketNewLoading, marketNewErrorCode, marketNewData, fetchMarketNewData] = useFetchData();
    const [marketMostPlayedLoading, marketMostPlayedErrorCode, marketMostPlayedData, fetchMarketMostPlayedData] = useFetchData();
    const [competitorNewLoading, competitorNewErrorCode, competitorNewData, fetchCompetitorNewData] = useFetchData();
    const [competitorMostPlayedLoading, competitorMostPlayedErrorCode, competitorMostPlayedData, fetchCompetitorMostPlayedData] = useFetchData();
    const [mostPlayedSongsLoading, mostPlayedSongsErrorCode, mostPlayedSongsData, fetchMostPlayedSongsData] = useFetchData();
    const [topFlopSongsLoading, topFlopSongsErrorCode, topFlopSongsData, fetchTopFlopSongsData] = useFetchData();

    const radioLoadings = [daypartLoading, contentBalanceLoading, breaksLoading];
    const radioErrors = [daypartErrorCode, contentBalanceErrorCode, breaksErrorCode];
    const radioDatas = [daypartData, contentBalanceData, breaksData];

    const musicLoadings = [
        top50OverlapLoading,
        top50OverlapTrendLoading,
        allSongsOverlapLoading,
        allSongsOverlapTrendLoading,
        marketNewLoading,
        marketMostPlayedLoading,
        competitorNewLoading,
        competitorMostPlayedLoading,
        mostPlayedSongsLoading,
        topFlopSongsLoading,
    ];
    const musicErrors = [
        top50OverlapErrorCode,
        top50OverlapTrendErrorCode,
        allSongsOverlapErrorCode,
        allSongsOverlapTrendErrorCode,
        marketNewErrorCode,
        marketMostPlayedErrorCode,
        competitorNewErrorCode,
        competitorMostPlayedErrorCode,
        mostPlayedSongsErrorCode,
        topFlopSongsErrorCode,
    ];
    const musicDatas = [
        top50OverlapData,
        top50OverlapTrendData,
        allSongsOverlapData,
        allSongsOverlapTrendData,
        marketNewData,
        marketMostPlayedData,
        competitorNewData,
        competitorMostPlayedData,
        mostPlayedSongsData,
        topFlopSongsData,
    ];

    const anyLoading = (dashboardTypeLoaded === "radio" && radioLoadings.some(Boolean)) || (dashboardTypeLoaded === "music" && musicLoadings.some(Boolean));
    const allErrorCode = (dashboardTypeLoaded === "radio" && radioErrors.every(Boolean)) || (dashboardTypeLoaded === "music" && musicErrors.every(Boolean));
    const anyData = (dashboardTypeLoaded === "radio" && radioDatas.some(Boolean)) || (dashboardTypeLoaded === "music" && musicDatas.some(Boolean));

    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useSetDataTypeOnStationChange(setFormData, formData.station);

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        setDashboardTypeLoaded(formData.dashboardType);
        if (formData.dashboardType === "radio") {
            await Promise.all([
                fetchDaypartData(`/dashboard/radio/${formData.daypart[0].daypart_id}?data_type=${formData.dataType}`),
                fetchContentBalanceData(`/dashboard/radio/${formData.daypart[0].daypart_id}/content_balance/`),
                fetchBreaksData(`/dashboard/radio/${formData.daypart[0].daypart_id}/top_flop_breaks/?data_type=${formData.dataType}`),
            ]);
        } else if (formData.dashboardType === "music") {
            await Promise.all([
                fetchTop50OverlapData(`/dashboard/music/${formData.daypart[0].daypart_id}/overlap?limit=50`),
                fetchTop50OverlapTrendData(`/dashboard/music/${formData.daypart[0].daypart_id}/overlap/trend?limit=50`),
                fetchAllSongsOverlapData(`/dashboard/music/${formData.daypart[0].daypart_id}/overlap?no_weeks=4`),
                fetchAllSongsOverlapTrendData(`/dashboard/music/${formData.daypart[0].daypart_id}/overlap/trend`),
                fetchMarketNewData(`/dashboard/music/${formData.daypart[0].daypart_id}/market/new`),
                fetchMarketMostPlayedData(`/dashboard/music/${formData.daypart[0].daypart_id}/market/most_played`),
                fetchCompetitorNewData(`/dashboard/music/${formData.daypart[0].daypart_id}/competitor/new`),
                fetchCompetitorMostPlayedData(`/dashboard/music/${formData.daypart[0].daypart_id}/competitor/most_played`),
                fetchMostPlayedSongsData(`/dashboard/music/${formData.daypart[0].daypart_id}/most_played_songs/?data_type=${formData.dataType}`),
                fetchTopFlopSongsData(`/dashboard/music/${formData.daypart[0].daypart_id}/top_flop_songs/?data_type=${formData.dataType}`),
            ]);
        }
    }

    return (
        <ModulePage>
            <ModulePage.Title>Dashboard</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.station.length || !formData.daypart.length}>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        filterFunction={x => !x.mtt_only}
                        loadOnChange={loadFormData}
                    />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <DataTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <Form.Group as={Col} className="d-flex flex-column col-12 col-xl-auto">
                        <Form.Label>Dashboard type</Form.Label>
                        <ToggleButtonGroup
                            type={"radio"}
                            name="dashboardType"
                            value={formData.dashboardType}
                            onChange={value => setFormData({ ...formData, dashboardType: value })}
                            className="ra-switcher">
                            <ToggleButton id={`${moduleName}-dashboard-type-radio`} variant={"ra-dark-blue-outline"} value={"radio"}>
                                Radio
                            </ToggleButton>
                            <ToggleButton id={`${moduleName}-dashboard-type-music`} variant={"ra-dark-blue-outline"} value={"music"}>
                                Music
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Form.Group>
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent errorCode={allErrorCode} multiPanel>
                <DataContent.Placeholder show={!anyLoading && !anyData}>
                    <PageDesciption
                        title={"Dashboard"}
                        className="bg-white p-4 px-4"
                        whatList={[
                            "Shows a summary of how your station is performing",
                            "Check into “Radio” for listening behaviour or “Music” for rotations, playlists, and song development",
                            "A great place to start your weekly station checkup",
                        ]}
                        knowList={[
                            {
                                text: "Available dayparts are based on the station chosen. Create your own dayparts under “settings”",
                            },
                            { text: "The most recent available data is automatically shown" },
                            { text: "Listener group data (heavy, moderate, light) needs a full week to be calculated" },
                        ]}
                        // relatedLinks={[]}
                        videoSrc="https://www.youtube.com/embed/WCrazxAtgAg?si=0B--rRaqzLt0az3C"
                    />
                </DataContent.Placeholder>
                {(anyData || anyLoading) && (
                    <Container fluid>
                        {dashboardTypeLoaded === "radio" ? (
                            <DashboardRadio
                                daypartData={daypartData}
                                contentBalanceData={contentBalanceData}
                                breaksData={breaksData}
                                daypartErrorCode={daypartErrorCode}
                                contentBalanceErrorCode={contentBalanceErrorCode}
                                breaksErrorCode={breaksErrorCode}
                                daypartLoading={daypartLoading}
                                contentBalanceLoading={contentBalanceLoading}
                                breaksLoading={breaksLoading}
                            />
                        ) : (
                            <DashboardMusic
                                top50OverlapData={top50OverlapData}
                                top50OverlapErrorCode={top50OverlapErrorCode}
                                top50OverlapLoading={top50OverlapLoading}
                                top50OverlapTrendData={top50OverlapTrendData}
                                top50OverlapTrendErrorCode={top50OverlapTrendErrorCode}
                                top50OverlapTrendLoading={top50OverlapTrendLoading}
                                allSongsOverlapData={allSongsOverlapData}
                                allSongsOverlapErrorCode={allSongsOverlapErrorCode}
                                allSongsOverlapLoading={allSongsOverlapLoading}
                                allSongsOverlapTrendData={allSongsOverlapTrendData}
                                allSongsOverlapTrendErrorCode={allSongsOverlapTrendErrorCode}
                                allSongsOverlapTrendLoading={allSongsOverlapTrendLoading}
                                marketNewData={marketNewData}
                                marketNewErrorCode={marketNewErrorCode}
                                marketNewLoading={marketNewLoading}
                                marketMostPlayedData={marketMostPlayedData}
                                marketMostPlayedErrorCode={marketMostPlayedErrorCode}
                                marketMostPlayedLoading={marketMostPlayedLoading}
                                competitorNewData={competitorNewData}
                                competitorNewErrorCode={competitorNewErrorCode}
                                competitorNewLoading={competitorNewLoading}
                                competitorMostPlayedData={competitorMostPlayedData}
                                competitorMostPlayedErrorCode={competitorMostPlayedErrorCode}
                                competitorMostPlayedLoading={competitorMostPlayedLoading}
                                mostPlayedSongsData={mostPlayedSongsData}
                                mostPlayedSongsErrorCode={mostPlayedSongsErrorCode}
                                mostPlayedSongsLoading={mostPlayedSongsLoading}
                                topFlopSongsData={topFlopSongsData}
                                topFlopSongsErrorCode={topFlopSongsErrorCode}
                                topFlopSongsLoading={topFlopSongsLoading}
                            />
                        )}
                    </Container>
                )}
            </ModulePage.DataContent>
        </ModulePage>
    );
}
